import { useEffect, useState } from "react";
import { useLenis } from "lenis/react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { Menu, X, CalendarCheck } from "lucide-react";
import { Logo } from "@/components/Logo";

export const CALENDLY_URL = "https://calendly.com/syndicappli-cguh/30min";

export const GRAD_BTN =
  "bg-[linear-gradient(100deg,#00C6CC_0%,#2FCB74_100%)] hover:opacity-90";

const LINKS = [
  { label: "Fonctionnalités", href: "#fonctionnalites" },
  { label: "Offres", href: "#offres" },
  { label: "Nous contacter", href: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const lenis = useLenis();
  const { pathname } = useLocation();
  // Logo blanc sur le dégradé du hero, logo couleur dès que le fond est clair
  const logoVariant = pathname === "/" && !scrolled ? "white" : "color";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (lenis) lenis.scrollTo(href, { offset: -80 });
    else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "border-b border-black/5 bg-white/85 backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3.5 lg:px-10">
        <a href="#" onClick={go("#accueil")} aria-label="Syndicappli360 — Accueil">
          <Logo variant={logoVariant} />
        </a>

        <div className="hidden items-center gap-9 md:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={go(l.href)}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              className="group relative text-sm font-semibold text-ink/70 transition-colors hover:text-ink"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-0.5 w-0 rounded-full bg-[linear-gradient(100deg,#00C6CC,#2FCB74)] transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-demo-button"
            className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-[0_10px_25px_-8px_rgba(0,190,200,0.6)] transition-transform duration-300 hover:scale-105 active:scale-95 ${GRAD_BTN}`}
          >
            <CalendarCheck size={16} />
            Réserver une démo
          </a>
        </div>

        <button
          className="rounded-full border border-ink/10 bg-white p-2.5 text-ink shadow-sm md:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="border-b border-black/5 bg-white/95 backdrop-blur-xl md:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={go(l.href)}
                  className="rounded-xl px-3 py-3 text-base font-semibold text-ink/80 hover:bg-mist"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-cta-demo-button"
                className={`mt-2 flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white ${GRAD_BTN}`}
              >
                <CalendarCheck size={16} />
                Réserver une démo
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
